import React from 'react';
import { Box, Container, Typography, Stack, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import GavelIcon from '@mui/icons-material/Gavel';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Header from './Header';
import SEO from './SEO';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';

const timelineEvents = [
  { time: 'Day 1', label: 'Incident occurs', detail: 'A staff member harms a child. No one catches it in time.' },
  { time: 'Day 2', label: 'Parents pull children', detail: 'Word spreads. Enrollment starts dropping immediately.' },
  { time: 'Week 1', label: 'Local news story', detail: 'TV cameras outside your door. Google reviews destroyed overnight.' },
  { time: 'Week 2', label: 'State investigation', detail: 'Licensing agency opens inquiry. Inspectors arrive unannounced.' },
  { time: 'Month 1', label: 'Lawsuit filed', detail: 'Family retains attorney. Suit names you personally, not just the business.' },
  { time: 'Month 3–18', label: 'Legal costs mount', detail: 'Defense attorney: $400+/hr. Discovery process drains reserves.' },
  { time: 'End result', label: 'Closure or bankruptcy', detail: 'Most independent operators cannot absorb a $262K–$16M judgment.' },
];

const settlementCases = [
  { amount: '$16M', label: 'SC infant death settlement', year: '2024', detail: 'Unsafe sleep practices led to an infant death. Single largest recorded daycare settlement.' },
  { amount: '$25M', label: 'Memphis lawsuit (filed)', year: '2025', detail: 'Two-year-old suffered permanent injuries and seizures from daycare worker abuse.' },
  { amount: '$475K', label: 'NYC brain damage case', year: '2021', detail: 'Seven-year-old suffered permanent brain damage after choking at a daycare facility.' },
];

const consequences = [
  'Immediate enrollment collapse — families leave the moment the story breaks',
  'Loss of childcare license — state agencies can suspend within days of an allegation',
  'Insurance premium spike or outright cancellation',
  'Personal liability — courts can pierce the corporate veil if negligence is systemic',
  'Criminal charges for staff who witnessed and failed to report',
  'Federal and state investigations running concurrently for years',
  'Permanent Google/Yelp reputation damage that outlasts any PR effort',
];

const headlines = [
  '"Daycare worker arrested after child found with bruises in 9 locations" — Rochester, MN 2025',
  '"51 criminal counts filed against preschool teacher who abused toddlers" — Centennial, CO 2025',
  '"Daycare worker charged after security footage shows aggression toward 2-year-old" — Blanding, UT 2026',
  '"Three Manhattan daycare workers charged with abusing 9 children over 18 months" — New York, NY 2025',
];

export default function ReputationPage() {
  const navigate = useNavigate();
  const scrollToContact = () => navigate('/contact');

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Protect Your Daycare's Reputation & Avoid Liability" description="One abuse incident costs far more than prevention. See the financial and reputational risks daycare operators face — and how SMCLA eliminates them." path="/reputation" />
      <Header onBookDemo={scrollToContact} />

      {/* Hero */}
      <Box sx={{ position: 'relative', py: { xs: 10, md: 14 }, px: 3, textAlign: 'center', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '-10%', left: '-5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(140,82,255,0.15) 0%, transparent 70%)', filter: 'blur(40px)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '-10%', right: '-5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.1) 0%, transparent 70%)', filter: 'blur(40px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <WarningAmberIcon sx={{ color: '#ff6b6b', fontSize: 14 }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#ff6b6b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Financial & Reputational Risk
            </Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '3.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 2.5, lineHeight: 1.1 }}>
            One bad hire can end<br /><Box component="span" sx={{ color: '#ff6b6b' }}>everything you built.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 560, mx: 'auto', lineHeight: 1.65, mb: 5 }}>
            Daycare abuse cases don't just harm children — they destroy businesses. Here's the full picture that every operator needs to understand before it's too late.
          </Typography>
          <Button variant="contained" size="large" onClick={scrollToContact} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}>
            Protect Your Business — Book a Demo →
          </Button>
        </Container>
      </Box>

      {/* Settlement Data */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 6, md: 10 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#ff6b6b', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Real Settlement Data
          </Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 1.5, letterSpacing: '-0.02em' }}>
            The numbers are devastating
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, mb: 6, maxWidth: 500, mx: 'auto' }}>
            These are real cases from 2021–2025. Every one of them was preventable.
          </Typography>
          <Grid container spacing={3}>
            {settlementCases.map(({ amount, label, year, detail }) => (
              <Grid key={label} size={{ xs: 12, md: 4 }}>
                <Box sx={{ bgcolor: '#13131f', border: '1px solid rgba(255,107,107,0.15)', borderRadius: 3, p: 3.5, height: '100%', textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '2.8rem' }, color: '#ff6b6b', lineHeight: 1, mb: 0.5 }}>
                    {amount}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.5 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#5ce1e6', mb: 1.5 }}>
                    {year}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>
                    {detail}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Typography sx={{ textAlign: 'center', mt: 3, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.5)' }}>
            Sources: The Benton Law Firm, Bob Katz Law, Arapahoe County CO, WCTV, ABC4 News
          </Typography>
        </Container>
      </Box>

      {/* Timeline */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            How It Unfolds
          </Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 6, letterSpacing: '-0.02em' }}>
            The timeline of destruction
          </Typography>
          <Stack spacing={0}>
            {timelineEvents.map(({ time, label, detail }, i) => (
              <Box key={time} sx={{ display: 'flex', gap: 3, position: 'relative' }}>
                {/* Connector line */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: i === timelineEvents.length - 1 ? 'rgba(255,107,107,0.15)' : '#13131f', border: `2px solid ${i === timelineEvents.length - 1 ? '#ff6b6b' : 'rgba(92,225,230,0.3)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1 }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 10, color: i === timelineEvents.length - 1 ? '#ff6b6b' : '#5ce1e6', textAlign: 'center', lineHeight: 1.1 }}>
                      {time.split(' ')[0]}<br />{time.split(' ').slice(1).join(' ')}
                    </Typography>
                  </Box>
                  {i < timelineEvents.length - 1 && (
                    <Box sx={{ width: 2, flex: 1, bgcolor: 'rgba(255,255,255,0.06)', minHeight: 32, my: 0.5 }} />
                  )}
                </Box>
                <Box sx={{ pb: i < timelineEvents.length - 1 ? 4 : 0, pt: 0.75 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: i === timelineEvents.length - 1 ? '#ff6b6b' : '#f0f0f8', mb: 0.5 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>
                    {detail}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Consequences */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={6} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'inline-flex', p: 2, bgcolor: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.15)', borderRadius: 2, mb: 3 }}>
                <GavelIcon sx={{ color: '#ff6b6b', fontSize: 32 }} />
              </Box>
              <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
                What this means for your business
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7 }}>
                A single incident doesn't just create legal liability. It triggers a cascade of simultaneous crises that most independent operators cannot survive.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                {consequences.map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ff6b6b', mt: 0.8, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#d0d0e0', lineHeight: 1.65 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* News Headlines */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'inline-flex', p: 2, bgcolor: 'rgba(148,153,176,0.06)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, mb: 3 }}>
            <NewspaperIcon sx={{ color: '#9499b0', fontSize: 32 }} />
          </Box>
          <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            These are your future headlines — if you do nothing
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', mb: 5, lineHeight: 1.7, maxWidth: 560 }}>
            Every one of these was a real story. Every one of them was a daycare operator who thought it couldn't happen to them.
          </Typography>
          <Stack spacing={2}>
            {headlines.map((headline, i) => (
              <Box key={i} sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid rgba(255,107,107,0.5)', borderRadius: 2 }}>
                <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontStyle: 'italic', fontSize: { xs: 14, md: 16 }, color: '#d0d0e0', lineHeight: 1.55 }}>
                  {headline}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* SMCLA as insurance */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(92,225,230,0.1)', py: { xs: 8, md: 12 }, px: 3, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <TrendingDownIcon sx={{ color: '#5ce1e6', fontSize: 48, mb: 2 }} />
          <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            SMCLA costs less than<br />one hour in court.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 17, color: '#9499b0', lineHeight: 1.7, mb: 5 }}>
            Attorney fees start at $400/hr. A trial runs months. One settlement averages $262,000 for physical abuse cases. SMCLA is the cheapest insurance policy your daycare will ever buy — and the only one that actually prevents the incident, not just pays after it.
          </Typography>
          <Button variant="contained" size="large" onClick={scrollToContact} sx={{ px: 5, py: 1.75, fontWeight: 700, fontSize: 17, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 32px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}>
            Book a Demo — Protect Your Business →
          </Button>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
